import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Alert,
  useTheme,
  alpha,
  LinearProgress,
} from "@mui/material";
import {
  AccessTime as ClockIcon,
  Check as CheckIcon,
  Visibility as EyeIcon,
  Download as DownloadIcon,
  Person as UserIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";

interface TimesheetEntry {
  id: number;
  employeeName: string;
  employeeId: string;
  weekPeriod: string;
  hoursWorked: number;
  overtimeHours: number;
  status: "Submitted" | "Pending" | "Approved" | "Rejected";
  submissionDate: string;
  expectedHours: number;
  utilization: number;
}

const TimesheetsTab: React.FC = () => {
  const theme = useTheme();
  const [successMessage, setSuccessMessage] = useState("");

  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([
    {
      id: 1,
      employeeName: "Sarah Johnson",
      employeeId: "EMP001",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 42,
      overtimeHours: 2,
      status: "Pending",
      submissionDate: "2024-12-30",
      expectedHours: 40,
      utilization: 105,
    },
    {
      id: 2,
      employeeName: "Mike Chen",
      employeeId: "EMP002",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 38,
      overtimeHours: 0,
      status: "Pending",
      submissionDate: "2024-12-30",
      expectedHours: 40,
      utilization: 95,
    },
    {
      id: 3,
      employeeName: "Emily Davis",
      employeeId: "EMP003",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 45,
      overtimeHours: 5,
      status: "Pending",
      submissionDate: "2024-12-29",
      expectedHours: 40,
      utilization: 112,
    },
    {
      id: 4,
      employeeName: "James Wilson",
      employeeId: "EMP004",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 40,
      overtimeHours: 0,
      status: "Pending",
      submissionDate: "2024-12-30",
      expectedHours: 40,
      utilization: 100,
    },
    {
      id: 5,
      employeeName: "Lisa Brown",
      employeeId: "EMP005",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 35,
      overtimeHours: 0,
      status: "Submitted",
      submissionDate: "2024-12-30",
      expectedHours: 40,
      utilization: 87,
    },
    {
      id: 6,
      employeeName: "David Kim",
      employeeId: "EMP006",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 48,
      overtimeHours: 8,
      status: "Pending",
      submissionDate: "2024-12-29",
      expectedHours: 40,
      utilization: 120,
    },
    {
      id: 7,
      employeeName: "Anna Rodriguez",
      employeeId: "EMP007",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 41,
      overtimeHours: 1,
      status: "Pending",
      submissionDate: "2024-12-30",
      expectedHours: 40,
      utilization: 102,
    },
    {
      id: 8,
      employeeName: "Tom Anderson",
      employeeId: "EMP008",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 37,
      overtimeHours: 0,
      status: "Submitted",
      submissionDate: "2024-12-30",
      expectedHours: 40,
      utilization: 92,
    },
    {
      id: 9,
      employeeName: "Rachel Green",
      employeeId: "EMP009",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 44,
      overtimeHours: 4,
      status: "Pending",
      submissionDate: "2024-12-29",
      expectedHours: 40,
      utilization: 110,
    },
    {
      id: 10,
      employeeName: "Chris Martinez",
      employeeId: "EMP010",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 39,
      overtimeHours: 0,
      status: "Pending",
      submissionDate: "2024-12-30",
      expectedHours: 40,
      utilization: 97,
    },
    {
      id: 11,
      employeeName: "Jennifer Lee",
      employeeId: "EMP011",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 46,
      overtimeHours: 6,
      status: "Pending",
      submissionDate: "2024-12-29",
      expectedHours: 40,
      utilization: 115,
    },
    {
      id: 12,
      employeeName: "Alex Thompson",
      employeeId: "EMP012",
      weekPeriod: "Dec 23 - Dec 29, 2024",
      hoursWorked: 36,
      overtimeHours: 0,
      status: "Submitted",
      submissionDate: "2024-12-30",
      expectedHours: 40,
      utilization: 90,
    },
  ]);

  const pendingCount = timesheets.filter(ts =>
    ts.status === "Pending" || ts.status === "Submitted"
  ).length;

  const handleApprove = (id: number) => {
    setTimesheets(prev =>
      prev.map(ts =>
        ts.id === id ? { ...ts, status: "Approved" as const } : ts
      )
    );
    setSuccessMessage("Timesheet approved successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleViewDetails = (id: number) => {
    console.log(`Viewing timesheet details for ${id}`);
    setSuccessMessage("Opening timesheet details...");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleExportAll = () => {
    console.log("Exporting all timesheets");
    setSuccessMessage("Exporting all timesheets to CSV...");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Submitted":
        return "info";
      case "Pending":
        return "warning";
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization >= 110) return "error";
    if (utilization >= 100) return "success";
    if (utilization >= 90) return "warning";
    return "info";
  };

  return (
    <Box>
      {/* Header with Stats */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <ClockIcon color="primary" sx={{ mr: 1 }} />
            Timesheets
            {pendingCount > 0 && (
              <Chip
                label={pendingCount}
                color="error"
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pendingCount} timesheets pending review and approval
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportAll}
        >
          Export All
        </Button>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Timesheets Grid */}
      <Grid container spacing={2}>
        {timesheets.map((timesheet) => (
          <Grid item xs={12} md={6} lg={4} key={timesheet.id}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: theme.shadows[2],
                border: timesheet.utilization >= 110 ? `2px solid ${theme.palette.error.main}` :
                        timesheet.status === "Pending" ? `2px solid ${theme.palette.warning.main}` : "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      <UserIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {timesheet.employeeName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {timesheet.employeeId}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={timesheet.status}
                    color={getStatusColor(timesheet.status) as any}
                    variant="outlined"
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Week:</strong> {timesheet.weekPeriod}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Hours Worked:</strong> {timesheet.hoursWorked}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Overtime:</strong> {timesheet.overtimeHours}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Submitted:</strong> {timesheet.submissionDate}
                  </Typography>
                </Box>

                {/* Utilization Progress */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Utilization
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <TrendingUpIcon
                        color={getUtilizationColor(timesheet.utilization) as any}
                        sx={{ fontSize: 16, mr: 0.5 }}
                      />
                      <Typography
                        variant="body2"
                        color={`${getUtilizationColor(timesheet.utilization)}.main`}
                        fontWeight={500}
                      >
                        {timesheet.utilization}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(timesheet.utilization, 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[300], 0.3),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: (() => {
                          const color = getUtilizationColor(timesheet.utilization);
                          return theme.palette[color as 'success' | 'info' | 'warning' | 'error']?.main || theme.palette.primary.main;
                        })(),
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<EyeIcon />}
                    onClick={() => handleViewDetails(timesheet.id)}
                    fullWidth
                  >
                    View Details
                  </Button>
                  {(timesheet.status === "Pending" || timesheet.status === "Submitted") && (
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckIcon />}
                      onClick={() => handleApprove(timesheet.id)}
                      fullWidth
                    >
                      Approve
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {timesheets.length === 0 && (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <ClockIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No timesheets found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Employee timesheets will appear here when submitted.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TimesheetsTab;